import { currentStreak, type DateKey, localDateKey } from '@arrow/game-engine';
import { useEffect, useMemo, useState } from 'react';

import { type Boot, boot } from './app/boot.ts';
import { type Prefs, prefs as appPrefs } from './app/prefs.ts';
import { goUp, navigate, navigations, routePath, syncLocation, useRoute, useRouteFocus } from './app/router.tsx';
import type { DevControls } from './dev/types.ts';
import { DevContext } from './app/dev-context.ts';
import { setDevMode, useDevMode, devToolsToken, setDevToolsToken } from './dev/mode.ts';
import { useNestsVisible } from './nests/store.ts';
import { NotifySheetHost } from './push/host.tsx';
import { setPlayedSource } from './push/push.ts';
import { playedState } from './push/state.ts';
import { ChallengeScreen } from './screens/Challenge.tsx';
import { DailyScreen, PracticeScreen } from './screens/GameScreen.tsx';
import { NestScreen } from './screens/Nest.tsx';
import { NestInvite } from './screens/NestInvite.tsx';
import { Play } from './screens/Play.tsx';
import { Privacy } from './screens/Privacy.tsx';
import { Stats } from './screens/Stats.tsx';
import { Today } from './screens/Today.tsx';
import { createPersistence, type Persistence } from './storage/persistence.ts';
import './styles/screens.css';

interface AppProps {
	persistence?: Persistence;
	prefs?: Prefs;
	/** Tests: the boot (platform info + share params) to use instead of the real one. */
	boot?: Boot;
}

/**
 * The shell: boots the platform once, reads the route and shows Today, a game (a daily by
 * number, or a practice puzzle), Play, Nest (behind its gate, nests/store.ts), Stats, a
 * challenge or a nest invite. The tab bar lives on the three non-game
 * screens; a game is all board, with "‹ Today" top left. History keeps Today as the root
 * (app/router.tsx): back from anywhere ends on Today, and back from Today leaves.
 */
export function App({ persistence: injected, prefs: injectedPrefs, boot: injectedBoot }: AppProps) {
	const persistence = useMemo(() => injected ?? createPersistence(), [injected]);
	const prefs = useMemo(() => injectedPrefs ?? appPrefs(), [injectedPrefs]);
	const [realToday, setRealToday] = useState(() => localDateKey(new Date()));
	// Dev mode (dev/mode.ts): off for players; the dev tools' date override only applies while it's on.
	const devMode = useDevMode();
	const [storedOverride, setDateOverride] = useState<DateKey | null>(() => persistence.devDate());
	const dateOverride = devMode ? storedOverride : null;
	const todayKey = dateOverride ?? realToday;
	const [booted] = useState<Boot>(() => injectedBoot ?? boot(todayKey));
	// What notifications tell the api about play (push/push.ts): the device's own date, never the dev override.
	useEffect(() => {
		setPlayedSource(() => {
			const stats = persistence.loadStats();
			return playedState(stats, currentStreak(stats, localDateKey(new Date())));
		});
	}, [persistence]);
	// `boot` may have rewritten the address bar (share params): read it again.
	useEffect(() => syncLocation(), []);
	const route = useRoute();
	// A new page: focus its heading (not on the first page, not into a game).
	useRouteFocus(route);
	const [bootMoves] = useState(navigations);
	// Nests (nests/store.ts): the Nest tab shows with dev mode or to a member of a nest.
	const nestsVisible = useNestsVisible();
	// Behind the gate /nest is Today (and leaving your last nest lands there): say so in the address.
	const nestHidden = route.name === 'nest' && !nestsVisible;
	useEffect(() => {
		if (nestHidden) goUp('/');
	}, [nestHidden]);

	// A new day starts when the player comes back after midnight.
	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState === 'visible') setRealToday(localDateKey(new Date()));
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => document.removeEventListener('visibilitychange', onVisible);
	}, []);

	const dev = useMemo<DevControls | undefined>(
		() =>
			devMode
				? {
						realTodayKey: realToday,
						dateOverride,
						setDateOverride: (key) => {
							persistence.setDevDate(key);
							setDateOverride(key);
						},
						puzzleId: route.name === 'practice' ? route.id : null,
						selectPuzzle: (id) => navigate(id === null ? '/' : routePath({ name: 'practice', id, level: null })),
						clearAll: () => {
							// A clean slate for the game, but this device stays in dev mode with its token.
							const token = devToolsToken();
							persistence.clearAll();
							setDevMode(true);
							setDevToolsToken(token);
							window.location.reload();
						},
						turnOff: () => {
							persistence.setDevDate(null);
							setDateOverride(null);
							setDevMode(false);
						},
					}
				: undefined,
		[dateOverride, devMode, persistence, realToday, route],
	);

	const stats = persistence.loadStats();
	// A first visit: nothing played on this device and no level remembered.
	const firstVisit = prefs.lastLevel() === null && Object.keys(stats.days).length === 0 && stats.practice.completed === 0;
	// The share link this page was opened with is still the page on screen.
	const fromShare = booted.share.fromShare && navigations() === bootMoves;

	// Behind the gate, /nest is Today.
	const shown = nestHidden ? ({ name: 'today' } as const) : route;
	// Every screen's ≡ menu reaches the dev tools through this (app/dev-context.ts).
	return (
		<DevContext.Provider value={dev}>
			{screenFor()}
			{/* The notifications sheet, from the bell, a moment or the streak (push/host.tsx). */}
			<NotifySheetHost />
		</DevContext.Provider>
	);

	function screenFor() {
		switch (shown.name) {
			case 'daily':
				return (
					<DailyScreen
						key={`${shown.number}:${todayKey}`}
						number={shown.number}
						level={shown.level}
						todayKey={todayKey}
						persistence={persistence}
						prefs={prefs}
						via={fromShare ? 'share' : 'link'}
						ctx={shown.ctx ?? null}
						firstSeen={booted.info.firstSeen}
						firstShareVisit={fromShare && !booted.info.isReturning}
						dev={dev}
					/>
				);
			case 'practice':
				return (
					<PracticeScreen
						key={shown.id}
						id={shown.id}
						todayKey={todayKey}
						persistence={persistence}
						dev={dev}
						ctx={shown.ctx ?? null}
						firstSeen={booted.info.firstSeen}
					/>
				);
			case 'challenge':
				return (
					<ChallengeScreen
						key={shown.code}
						code={shown.code}
						todayKey={todayKey}
						persistence={persistence}
						dev={dev ? { message: true } : undefined}
					/>
				);
			case 'play':
				return <Play todayKey={todayKey} persistence={persistence} prefs={prefs} firstSeen={booted.info.firstSeen} params={shown.params} />;
			case 'invite':
				// Open to anyone with the link, dev mode or not. Nest dates are always the device's own.
				return <NestInvite key={shown.code} code={shown.code} by={shown.by} todayKey={realToday} />;
			case 'nest':
				return <NestScreen todayKey={realToday} persistence={persistence} />;
			case 'privacy':
				return <Privacy todayKey={todayKey} />;
			case 'stats':
				return <Stats todayKey={todayKey} persistence={persistence} />;
			default:
				return (
					<Today
						key={todayKey}
						todayKey={todayKey}
						persistence={persistence}
						prefs={prefs}
						firstVisit={firstVisit}
						dev={
							dev
								? {
										message: true,
										back: dev.dateOverride ? { label: 'Back to today (dev)', onClick: () => dev.setDateOverride(null) } : undefined,
									}
								: undefined
						}
					/>
				);
		}
	}
}
